// Login Page - Admin authentication with email/password and Google
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    Divider,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const { loginWithEmail, loginWithGoogle, resetPassword } = useAuth();
    const navigate = useNavigate();

    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            await loginWithEmail(email, password);
            navigate('/');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            await loginWithGoogle();
            navigate('/');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!resetEmail.trim()) return;
        setResetLoading(true);
        try {
            await resetPassword(resetEmail.trim());
            setResetDialogOpen(false);
            setResetEmail('');
            setError('');
            setSuccessMessage('Password reset email sent! Check your inbox.');
        } catch (err) {
            setError(err.message);
            setResetDialogOpen(false);
        } finally {
            setResetLoading(false);
        }
    };

    const openResetDialog = () => {
        setResetEmail(email); // Pre-fill with current email if entered
        setResetDialogOpen(true);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #FF6B9D 0%, #FFB8B8 50%, #FFF5F5 100%)',
                p: 2
            }}
        >
            <Card sx={{ maxWidth: 400, width: '100%', boxShadow: 6 }}>
                <CardContent sx={{ p: 4 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <AdminPanelSettingsIcon sx={{ fontSize: 64, color: '#FF6B9D', mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                            Ovuloom Admin
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Moderation & Management Dashboard
                        </Typography>
                    </Box>

                    {/* Success Alert */}
                    {successMessage && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
                            {successMessage}
                        </Alert>
                    )}

                    {/* Error Alert */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {/* Email/Password Form */}
                    <Box component="form" onSubmit={handleEmailLogin}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin="normal"
                            required
                            disabled={loading}
                        />
                        <TextField
                            fullWidth
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            margin="normal"
                            required
                            disabled={loading}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                            <Button
                                size="small"
                                onClick={openResetDialog}
                                disabled={loading}
                                sx={{ color: '#FF6B9D', textTransform: 'none', fontSize: '0.8rem' }}
                            >
                                Forgot Password?
                            </Button>
                        </Box>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{
                                mt: 1,
                                py: 1.5,
                                bgcolor: '#FF6B9D',
                                '&:hover': { bgcolor: '#E55A8A' }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                        </Button>
                    </Box>

                    {/* Divider */}
                    <Divider sx={{ my: 3 }}>or</Divider>

                    {/* Google Login */}
                    <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        startIcon={<GoogleIcon />}
                        onClick={handleGoogleLogin}
                        disabled={loading}
                        sx={{ py: 1.5 }}
                    >
                        Sign in with Google
                    </Button>

                    {/* Footer */}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', textAlign: 'center', mt: 3 }}
                    >
                        Admin access only. Unauthorized access is prohibited.
                    </Typography>
                </CardContent>
            </Card>

            {/* Password Reset Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter your admin email address and we'll send you a password reset link.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        disabled={resetLoading}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogOpen(false)} disabled={resetLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResetPassword}
                        variant="contained"
                        disabled={resetLoading || !resetEmail.trim()}
                        sx={{ bgcolor: '#FF6B9D', '&:hover': { bgcolor: '#E55A8A' } }}
                    >
                        {resetLoading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Login;
